import { useEffect, useRef, useState } from 'react'
import { TESTPHASE_TAGE, type SessionUser } from '@raporto/shared'
import { Marke } from './Marke.js'
import { api } from '../net/apiClient.js'

/**
 * «Einrichtung wird abgeschlossen.»
 *
 * Der Name ist `Checkoutrueckkehr` und nicht `Einrichtung`: `Einrichtung`
 * heisst bereits die Liste in den Einstellungen, die einem bestehenden
 * Betrieb zeigt, was ihm noch fehlt. Zwei Bildschirme mit demselben Namen im
 * selben Ordner sind eine Verwechslung, die genau einmal passieren muss.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Warum es diese Seite überhaupt gibt
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Stripe schickt den Browser zurück, sobald die Zahlungsmethode hinterlegt
 * ist. Das ist nicht derselbe Moment, in dem Raporto davon erfährt: Die
 * Bestätigung kommt als Webhook, von Stripe an unseren Server, auf einem
 * anderen Weg und ein bis zwei Sekunden später.
 *
 * Diese Seite überbrückt genau diese Lücke. Sie tut dabei ausdrücklich
 * NICHTS: Sie legt nichts an, schaltet nichts frei und entscheidet nichts.
 * Sie fragt nach und wartet.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * Warum die Rückkehradresse nichts freischalten darf
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Eine Adresse, die einen Betrieb anlegt, ist eine Adresse, die jemand ein
 * zweites Mal aufrufen kann – aus dem Verlauf, aus einem Lesezeichen, aus
 * einer weitergeleiteten Mail. Die Wahrheit über eine Zahlung steht bei
 * Stripe und kommt signiert. Was diese Seite zeigt, hat der Webhook
 * entschieden.
 *
 * Der Schlüssel in der Adresse ist deshalb auch keine Berechtigung für den
 * Betrieb, sondern nur für die Anmeldung an dem einen Betrieb, den DIESER
 * Vorgang angelegt hat – einmalig, und nur nachdem er entstanden ist.
 */

/*
  Wie oft nachgefragt wird – am Anfang schneller.

  Der Normalfall ist, dass der Webhook schon da ist, bevor der Browser
  zurückkommt: Serverseitig gemessen dauert der ganze Abschluss unter einer
  Zehntelsekunde. Mit einem starren Zweisekundentakt wartete der Kunde
  trotzdem im Schnitt eine Sekunde auf eine Antwort, die längst feststand.

  Deshalb die erste halbe Minute im Sekundentakt, danach zwei Sekunden. Das
  kostet ein paar Abfragen mehr und spart dem Kunden genau die Sekunden, in
  denen er sich fragt, ob etwas hängt.
*/
const TAKT_SCHNELL_MS = 1000
const TAKT_RUHIG_MS = 2000
const SCHNELL_BIS_MS = 30_000

/*
  Wann aufgegeben wird.

  Nach einer Minute ist nicht der Webhook langsam, sondern etwas kaputt. Dann
  ist die ehrliche Auskunft besser als ein Kreisel, der sich weiterdreht: Die
  Zahlung ist erfasst, der Betrieb kommt, und die Anmeldung geht danach über
  das normale Formular.
*/
const GEDULD_MS = 60_000

export function Checkoutrueckkehr({
  aufAngemeldet,
  aufLogin,
  aufStart
}: {
  aufAngemeldet: (s: SessionUser) => void
  aufLogin: () => void
  aufStart: () => void
}): JSX.Element {
  const [lage, setLage] = useState<'wartet' | 'zulange' | 'unbekannt'>('wartet')
  /* Die Anmeldeadresse – sie ist die häufigste Frage direkt nach der
     Einrichtung, und hier ist der Moment, in dem sie jemand liest. */
  const [anmeldemail, setAnmeldemail] = useState('')
  /* Der Timer soll bei jedem Neurendern derselbe bleiben. */
  const seit = useRef(Date.now())

  useEffect(() => {
    const q = new URLSearchParams(window.location.search)
    const sitzung = q.get('sitzung') ?? ''
    const schluessel = q.get('schluessel') ?? ''
    if (!sitzung) {
      setLage('unbekannt')
      return
    }

    let gestoppt = false

    /*
      Der Schlüssel wird aus der Adresszeile entfernt, sobald er gelesen ist.

      Er ist einmalig und kurzlebig, aber er soll auch nicht im Verlauf des
      Browsers, in einem Lesezeichen oder im Referrer der nächsten Seite
      stehen. `replaceState` schreibt den Verlaufseintrag um, statt einen
      zweiten anzulegen – ein «Zurück» führt danach nicht zur Adresse mit dem
      Schlüssel.
    */
    if (schluessel) {
      const sauber = `${window.location.pathname}?sitzung=${encodeURIComponent(sitzung)}`
      window.history.replaceState({}, '', sauber)
    }

    const fragen = async (): Promise<void> => {
      if (gestoppt) return
      try {
        const r = await api.einrichtungsstand(sitzung, schluessel)
        if (gestoppt) return
        if (r.anmeldeEmail) setAnmeldemail(r.anmeldeEmail)
        if (r.session) {
          gestoppt = true
          aufAngemeldet(r.session as SessionUser)
          return
        }
        if (r.fertig) {
          /* Der Betrieb steht, aber ohne gültigen Schlüssel gibt es hier
             keine Anmeldung. Das Formular ist der richtige Weg. */
          gestoppt = true
          setLage('zulange')
          return
        }
        if (r.status === 'abgelaufen' || r.status === 'unbekannt') {
          gestoppt = true
          setLage('unbekannt')
          return
        }
      } catch {
        /* Ein Aussetzer beim Nachfragen ist kein Ergebnis. Weiterfragen. */
      }
      if (Date.now() - seit.current > GEDULD_MS) {
        gestoppt = true
        setLage('zulange')
      }
    }

    void fragen()
    /*
      `setTimeout` in Kette statt `setInterval`.

      Der Takt ändert sich unterwegs, und ein Intervall lässt sich nicht
      umstellen, ohne es abzuräumen. Die Kette fragt sich bei jedem Schritt
      selbst, wie lange sie wartet.
    */
    let wecker: ReturnType<typeof setTimeout> | null = null
    const weiter = (): void => {
      if (gestoppt) return
      const takt = Date.now() - seit.current < SCHNELL_BIS_MS ? TAKT_SCHNELL_MS : TAKT_RUHIG_MS
      wecker = setTimeout(() => {
        void fragen().then(weiter)
      }, takt)
    }
    weiter()
    return () => {
      gestoppt = true
      if (wecker) clearTimeout(wecker)
    }
  }, [aufAngemeldet])

  return (
    <div className="rp-public rp-einrichtung">
      <header className="rp-best-kopf">
        <button type="button" className="rp-marke" onClick={aufStart} id="btn-marke">
          <Marke groesse={26} />
        </button>
      </header>

      <main className="rp-best-blatt">
        <section className="rp-best-teil" id="einrichtung">
          {lage === 'wartet' && (
            <>
              <div className="rp-einr-kreisel" aria-hidden="true" />
              <h1 id="einrichtung-titel">Willkommen bei Raporto</h1>
              <p className="rp-best-unter">
                {/*
                  Die Dauer kommt aus der Preisliste. Getippt stand hier
                  «30-tägige Testphase» – auf dem Bildschirm unmittelbar nach
                  dem Hinterlegen der Karte, also genau dort, wo man es
                  glaubt.
                */}
                Deine Zahlungsmethode ist hinterlegt, und deine Testphase über {TESTPHASE_TAGE} Tage
                ist gestartet. Kosten entstehen heute keine. Dein Betrieb wird gerade eingerichtet –
                das dauert einen Moment, die Seite öffnet ihn von selbst.
              </p>
              {anmeldemail && (
                <p className="rp-best-unter" id="willkommen-anmeldemail">
                  Deine Anmelde-E-Mail: <strong>{anmeldemail}</strong>
                </p>
              )}
              <p className="rp-best-hinweis">
                Bitte nicht schliessen und nicht neu laden.
              </p>
            </>
          )}

          {lage === 'zulange' && (
            <>
              <h1 id="einrichtung-titel">Dein Betrieb ist bereit</h1>
              <p className="rp-best-unter">
                Die Anmeldung von hier aus hat nicht geklappt. Melde dich mit deiner Anmelde-E-Mail
                und dem Passwort an, das du eben vergeben hast – dein Betrieb wartet dahinter.
                Deine Testphase läuft bereits, Kosten sind keine entstanden.
              </p>
              {anmeldemail && (
                <p className="rp-best-unter" id="willkommen-anmeldemail">
                  Deine Anmelde-E-Mail: <strong>{anmeldemail}</strong>
                </p>
              )}
              <div className="rp-best-fuss">
                <button
                  type="button"
                  className="rp-knopf rp-knopf-gold rp-knopf-gross"
                  id="btn-einr-login"
                  onClick={aufLogin}
                >
                  Zur Anmeldung
                </button>
              </div>
            </>
          )}

          {lage === 'unbekannt' && (
            <>
              <h1 id="einrichtung-titel">Dieser Vorgang ist abgelaufen</h1>
              <p className="rp-best-unter">
                Zu dieser Adresse ist keine Einrichtung mehr offen. Besteht dein Betrieb bereits,
                kommst du über die Anmeldung hinein; sonst kannst du die Bestellung neu beginnen.
                Kosten sind in keinem Fall entstanden.
              </p>
              <div className="rp-best-fuss">
                <button
                  type="button"
                  className="rp-textknopf"
                  id="btn-einr-start"
                  onClick={aufStart}
                >
                  Zur Startseite
                </button>
                <button
                  type="button"
                  className="rp-knopf rp-knopf-gold rp-knopf-gross"
                  id="btn-einr-login"
                  onClick={aufLogin}
                >
                  Zur Anmeldung
                </button>
              </div>
            </>
          )}
        </section>
      </main>
    </div>
  )
}
